import React, { useState, useCallback } from 'react';
import PhoneInput from 'react-phone-number-input/mobile';
import { Auth } from 'aws-amplify';
import Loader from '@ts/ui/Loader';
import { userUpdate } from '@ts/utils/iterableUtils';
import { set, LS_COGNITO_SIGN_UP } from '@ts/utils/ls';
import { forms } from './data';
import * as S from './styles';

function SignUpForm({ setActiveForm }) {
    const [formData, updateFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        phone: '',
        password: '',
    });
    const [error, updateError] = useState('');
    const [isSended, updateIsSended] = useState(false);
    const [isLoading, updateIsLoading] = useState(false);

    const validateForm = useCallback(() => {
        if (!formData.email) {
            updateError('Email is required');
            return false;
        }
        if (!formData.firstname) {
            updateError('First Name is required');
            return false;
        }
        if (!formData.lastname) {
            updateError('Last Name is required');
            return false;
        }
        if (!formData.password) {
            updateError('Password is required');
            return false;
        }
        updateError('');
        return true;
    }, [formData, updateError]);

    const onChange = useCallback(
        ({ target }) =>
            updateFormData({
                ...formData,
                [target.name]: target.value,
            }),
        [formData, updateFormData]
    );
    const onChangePhone = useCallback(
        (value) =>
            updateFormData({
                ...formData,
                phone: value,
            }),
        [formData, updateFormData]
    );

    const onSubmit = useCallback(
        (event) => {
            event.preventDefault();
            const { email, firstname, lastname, phone, password } = formData;
            if (validateForm() && !isLoading) {
                updateIsLoading(true);
                Auth.signUp({
                    username: email,
                    password: password,
                    attributes: {
                        email: email,
                        given_name: firstname,
                        family_name: lastname,
                        phone_number: phone,
                    },
                })
                    .then(() => {
                        updateIsLoading(false);
                        updateIsSended(true);
                        set(LS_COGNITO_SIGN_UP, { email, code: password });
                        userUpdate(email, {
                            firstName: firstname,
                            lastName: lastname,
                            fullName: [firstname, lastname].filter(Boolean).join(' '),
                            phoneNumber: phone,
                        });
                    })
                    .catch((err) => {
                        updateError(err.message);
                        updateIsLoading(false);
                    });
            }
        },
        [validateForm, updateIsLoading, updateError, formData, isLoading, updateIsSended]
    );

    return isSended ? (
        <S.Form>
            <S.InnerTitle>We have sent a verification link to your email</S.InnerTitle>
            <S.InnerTitle>
                After verification, you can{' '}
                <S.BlueButton onClick={() => setActiveForm(forms.signin)}>sign in</S.BlueButton>{' '}
                with provided email and password
            </S.InnerTitle>
        </S.Form>
    ) : (
        <S.Form>
            <S.InnerTitle>Sign up with a new account</S.InnerTitle>
            {error && <S.Error>{error}</S.Error>}
            <S.Label htmlFor="Email">Email</S.Label>
            <S.Input
                placeholder="Email"
                type="email"
                id="Email"
                name="email"
                value={formData.email}
                onChange={onChange}
            />
            <S.Label htmlFor="Firstname">First name</S.Label>
            <S.Input
                placeholder="First name"
                type="text"
                id="Firstname"
                name="firstname"
                value={formData.firstname}
                onChange={onChange}
            />
            <S.Label htmlFor="Lastname">Last name</S.Label>
            <S.Input
                placeholder="Last name"
                type="text"
                id="Lastname"
                name="lastname"
                value={formData.lastname}
                onChange={onChange}
            />
            <S.Label htmlFor="Phone">Phone number</S.Label>
            <PhoneInput
                id="phone"
                placeholder="Phone number"
                showCountrySelect={false}
                value={formData.phone || ''}
                onChange={onChangePhone}
                country="US"
            />
            <S.Label htmlFor="Password">Password</S.Label>
            <S.Input
                placeholder="Password"
                type="password"
                id="Password"
                name="password"
                value={formData.password}
                onChange={onChange}
            />
            <S.SubmitButton onClick={onSubmit} disabled={isLoading}>
                {isLoading ? (
                    <S.LoaderWrap>
                        <Loader size="massive" />
                    </S.LoaderWrap>
                ) : (
                    'Sign up'
                )}
            </S.SubmitButton>
            <S.TextWrapper>
                <S.Text>Already have an account?</S.Text>
                <S.BlueButton onClick={() => setActiveForm(forms.signin)}>Sign in</S.BlueButton>
            </S.TextWrapper>
        </S.Form>
    );
}

export default SignUpForm;
export { SignUpForm };
